import { useI18n } from '@/shared/i18n';
import type { AccountStatementSummary as AccountStatementSummaryType } from '@yawi/types';
import { formatCurrency } from '@yawi/infrastructure';
import { AlertCircle, TrendingDown, TrendingUp, Wallet } from 'lucide-react';

interface AccountStatementSummaryProps {
  summary: AccountStatementSummaryType;
}

export const AccountStatementSummary = ({ summary }: AccountStatementSummaryProps) => {
  const { t } = useI18n();
  const cards = [
    {
      label: t('payments.currentBalance'),
      value: summary.currentBalance,
      icon: Wallet,
      color: summary.currentBalance > 0 ? 'text-red-600' : 'text-green-600',
      bgColor: summary.currentBalance > 0 ? 'bg-red-50' : 'bg-green-50',
    },
    {
      label: t('payments.totalCharges'),
      value: summary.totalCharges,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: t('payments.totalPayments'),
      value: summary.totalPayments,
      icon: TrendingDown,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      label: t('payments.overdueAmount'),
      value: summary.overdueAmount,
      icon: AlertCircle,
      color: summary.overdueAmount > 0 ? 'text-amber-600' : 'text-slate-400',
      bgColor: summary.overdueAmount > 0 ? 'bg-amber-50' : 'bg-slate-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.label} className={`${card.bgColor} rounded-xl p-4`}>
          <div className="flex items-center gap-2 mb-2">
            <card.icon size={18} className={card.color} />
            <span className="text-sm text-slate-600">{card.label}</span>
          </div>
          <p className={`text-xl font-bold ${card.color}`}>
            {formatCurrency(card.value)}
          </p>
        </div>
      ))}

      <div className="col-span-2 lg:col-span-4 flex gap-4 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-amber-500 rounded-full"></span>
          <span>{t('payments.overdueCount')}: {summary.overdueChargesCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
          <span>{t('payments.pendingCount')}: {summary.pendingChargesCount}</span>
        </div>
      </div>
    </div>
  );
};
